import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { Store } from '../../interfaces/store.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

type Form = { code: string; name: string; address: string; storeId: string };
const EMPTY: Form = { code: '', name: '', address: '', storeId: '' };

/**
 * F10 locations master. Editing needs FIXED_ASSET_MANAGE or FIXED_ASSET_SETTINGS_MANAGE (placement data, not accounting); a location is never deleted, only deactivated
 * (a deactivated location is no longer offered as a transfer target, assets already there keep it).
 */
const FixedAssetLocations: React.FC = () => {
  const canSettings = usePermission(PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE);
  const canAssetManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
  const canManage = canSettings || canAssetManage;
  const toast = useToast();
  const [rows, setRows] = useState<AssetLocation[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AssetLocation | 'new' | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setRows(await FixedAssetTransferService.locations());
    } catch (e) {
      toast.showApiError(e, 'Failed to load asset locations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // stores are only offered in the add/edit dialog, so only a manager loads (and hears about a failure of) them
    if (canManage) {
      getStoresByInstitutionId(0, 200, 'ALL').then(r => setStores(r?.content ?? []))
        .catch(e => { setStores([]); toast.showApiError(e, 'Failed to load stores'); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (row: AssetLocation | 'new') => {
    setEditing(row);
    setForm(row === 'new' ? EMPTY : {
      code: row.code, name: row.name, address: row.address ?? '', storeId: row.storeId ?? '',
    });
  };

  const save = async () => {
    if (!editing) return;
    const dto = {
      code: form.code.trim(), name: form.name.trim(),
      address: form.address.trim() || undefined, storeId: form.storeId || undefined,
    };
    try {
      setSaving(true);
      if (editing === 'new') await FixedAssetTransferService.createLocation(dto);
      else await FixedAssetTransferService.updateLocation(editing.id, dto);
      toast.showSuccess(editing === 'new' ? 'Location added' : 'Location updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: AssetLocation) => {
    try {
      await FixedAssetTransferService.setLocationActive(row.id, !row.active);
      toast.showSuccess(row.active ? 'Location deactivated' : 'Location activated');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to change location status');
    }
  };

  const storeName = (id?: string | null) => (id ? stores.find(s => s.id === id)?.storeName ?? '—' : '—');

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.loc.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h5">Asset Locations</Typography>
        {canManage && (
          <Button data-help="fixedAssets.loc.new" variant="contained" startIcon={<AddIcon />} onClick={() => open('new')}>Add location</Button>
        )}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table data-help-section="fixedAssets.loc.table">
            <TableHead>
              <TableRow>
                <TableCell data-help="fixedAssets.loc.col.code">Code</TableCell>
                <TableCell data-help="fixedAssets.loc.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.loc.col.address">Address</TableCell>
                <TableCell data-help="fixedAssets.loc.col.store">Store</TableCell>
                <TableCell data-help="fixedAssets.loc.col.status">Status</TableCell>
                {canManage && <TableCell data-help="fixedAssets.loc.col.actions" align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.code}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.address ?? '—'}</TableCell>
                  <TableCell>{storeName(r.storeId)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.active ? 'Active' : 'Inactive'} color={r.active ? 'success' : 'default'} />
                    {r.inUse && <Chip size="small" variant="outlined" label="In use" sx={{ ml: 1 }} />}
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Button data-help="fixedAssets.loc.action.edit" size="small" onClick={() => open(r)}>Edit</Button>
                      <Button data-help="fixedAssets.loc.action.toggle" size="small" color={r.active ? 'warning' : 'primary'} onClick={() => toggle(r)}>
                        {r.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No locations yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog data-help-page="fixedAssets.locForm.page" open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing === 'new' ? 'Add location' : 'Edit location'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField data-help="fixedAssets.locForm.field.code" label="Code" required fullWidth value={form.code} inputProps={{ maxLength: 30 }}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))} />
            <TextField data-help="fixedAssets.locForm.field.name" label="Name" required fullWidth value={form.name} inputProps={{ maxLength: 120 }}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField data-help="fixedAssets.locForm.field.address" label="Address" fullWidth multiline minRows={2} value={form.address} inputProps={{ maxLength: 300 }}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            <SearchableSelect<Store> data-help="fixedAssets.locForm.field.store"
              label="Linked store (optional)" options={stores} value={form.storeId}
              onChange={id => setForm(f => ({ ...f, storeId: id }))}
              getOptionLabel={s => s.storeName} getOptionId={s => s.id ?? ''} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.locForm.cancel" onClick={() => setEditing(null)}>Cancel</Button>
          <Button data-help="fixedAssets.loc.action.save" variant="contained" onClick={save} disabled={saving || !form.code.trim() || !form.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetLocations;
